import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Autocomplete,
  Alert,
  CircularProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  InputAdornment,
  Tooltip,
  Stack,
  Chip,
  Skeleton,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  FavoriteBorder as HeartIcon,
  Thermostat as TempIcon,
  MonitorWeight as WeightIcon,
  Height as HeightIcon,
  Air as LungIcon,
  Speed as SpeedIcon,
  Bloodtype as BloodIcon,
  ExpandMore as ExpandMoreAccordionIcon,
  NoteAdd as NoteAddIcon,
  Print as PrintIcon,
  Layers as LayersIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import Snackbar from '@mui/material/Snackbar';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import Cid10Autocomplete from './clinic/Cid10Autocomplete';
import { useAuth } from '../contexts/AuthContext';
import { useClinicPageBreakpoints } from '../hooks/useClinicPageBreakpoints';
import { useEvolutionCompactLayout } from '../hooks/useEvolutionCompactLayout';
import { useClinicMedicalSpecialty } from '../hooks/useClinicMedicalSpecialty';
import { getLoggedInProfessionalOption, mergeLoggedInProfessionalIntoOptions } from '../utils/loggedInProfessional';
import { prepareEvolutionFreeTextForEditor, sanitizeClinicalHtml } from '../utils/clinicalHtml';
import ClinicalHtmlEditor from './clinic/ClinicalHtmlEditor';
import { resolveEvolutionEntryFormat } from '../utils/clinicEvolutionFormat';
import {
  canEditEvolutionRecord,
  evolutionEditDeadline,
  evolutionEditTooltip,
  isEvolutionInvalidated,
} from '../utils/evolutionRecordPolicy';
import { printClinicalEvolutionBatchDocument, printClinicalEvolutionFullDocument } from '../utils/clinicalEvolutionFullPrint';
import { printClinicHtml } from '../utils/clinicPrintHtml';
import {
  buildCertificatePrintHtml,
  buildCidConsentBlockedNoticeHtml,
  isCidConsentRequiredError,
} from '../utils/certificatePrintDocument';
import { buildExamOrderPrintHtml } from '../utils/examOrderPrintDocument';
import { buildEvolutionTextDocBodyHtml } from '../utils/evolutionTextDocPrintDocument';
import {
  buildSignedPrescriptionNoticeHtml,
  resolvePrescriptionPrintPart,
} from '../utils/evolutionPrescriptionPrint';
import {
  buildSignedMedicalReportNoticeHtml,
  resolveMedicalReportPrintPart,
} from '../utils/evolutionMedicalReportPrint';
import { openSignedPrescriptionPdf, openSignedMedicalReportPdf } from '../services/documentSignatureService';
import EvolutionPrintablesWizard from './evolution/EvolutionPrintablesWizard';
import EvolutionAttachedPrintableChip from './evolution/EvolutionAttachedPrintableChip';
import EvolutionPrescriptionsSendButton from './evolution/EvolutionPrescriptionsSendButton';
import EvolutionPrintableEditHost from './evolution/EvolutionPrintableEditHost';
import PrescriptionTemplateChipPicker from './clinic/PrescriptionTemplateChipPicker';
import DocumentTextTemplateManagerDialog from './clinic/DocumentTextTemplateManagerDialog';
import EvolutionIndicationsDialog from './evolution/EvolutionIndicationsDialog';
import { buildEvolutionSavePayload, extractEvolutionIdFromResponse } from './evolution/buildEvolutionSavePayload';
import {
  evolutionDraftStatusLabel,
  useEvolutionDraftAutosave,
} from './evolution/useEvolutionDraftAutosave';
import { splitEvolutionWorkflowAssets, buildEvolutionPrintSnapshot, evolutionHasBatchPrintContent, evolutionHasFullPrintContent } from './evolution/evolutionViewHelpers';
import EvolutionOutcomeSection from './evolution/EvolutionOutcomeSection';
import EvolutionScheduleReturnPrompt from './evolution/EvolutionScheduleReturnPrompt';
import EvolutionInventoryConsumptionSection, {
  type EvolutionConsumptionLine,
} from './evolution/EvolutionInventoryConsumptionSection';
import EvolutionFileAttachmentsSection from './evolution/EvolutionFileAttachmentsSection';
import EvolutionAdditionsSection from './evolution/EvolutionAdditionsSection';
import EvolutionConsultAudioPanel from './evolution/EvolutionConsultAudioPanel';
import EvolutionAttendanceIdentificationSection from './evolution/EvolutionAttendanceIdentificationSection';
import EvolutionProcedureExecutionPanel from './evolution/EvolutionProcedureExecutionPanel';
import {
  appendPlanExecutionToFreeText,
  splitEvolutionProceduresAndPlanExecutions,
  type EvolutionPlanExecutionRecord,
} from './evolution/evolutionPlanExecution';
import { validateTraceabilityLines } from '../pages/Inventory/inventoryConstants';
import {
  defaultEvolutionOutcome,
  detectOutcomeScenario,
  fromLegacyReturnWorkflow,
  getOutcomeSchedulingSuggestion,
  parseSavedOutcome,
  resolveEffectiveOutcome,
  toLegacyReturnWorkflow,
  validateOutcome,
  type EvolutionOutcome,
  type OutcomeSchedulingSuggestion,
} from './evolution/evolutionOutcomeModel';
import {
  mergeEvolutionIndicationItems,
  parseEvolutionIndicationItems,
  type EvolutionAttachedPrintable,
  type EvolutionIndicationItem,
  type EvolutionPrintableCategory,
} from './evolution/evolutionWorkflowTypes';
import {
  buildClinicPrintDocumentHtml,
  buildClinicMultiSheetPrintHtml,
  buildEvolutionPrintAttachmentSection,
  buildClinicalPrintPatientInfoHtml,
  escapeHtml,
} from '../utils/clinicPrintLetterhead';
import {
  MOBILE_FORM_SURFACE_CLASS,
  clinicFormDialogRootSx,
  mobileFullScreenDialogPaperSx,
} from '../utils/mobileFormLayout';
import {
  EVOLUTION_THEME,
  evolutionCancelButtonSx,
  evolutionCaptionFaintSx,
  evolutionCaptionMutedSx,
  evolutionClickableRowSxFor,
  evolutionDialogContentSxFor,
  evolutionListDropzoneSx,
  evolutionDialogFooterSxFor,
  evolutionDialogPaperSx,
  evolutionSectionBodySx,
  evolutionSectionHeaderBarSxFor,
  evolutionSectionPaperSxFor,
  evolutionEmptyStateSx,
  evolutionLinkButtonSx,
  evolutionLoadingSkeletonSx,
  evolutionMonospaceBlockSx,
  evolutionOutlinedButtonSx,
  evolutionPrimaryButtonSx,
  evolutionSecondaryOutlinedButtonSx,
  evolutionSectionHeadingSx,
  evolutionSectionTitleSx,
  evolutionStatusBannerSx,
  evolutionStatusBannerTextSxFor,
} from './evolution/evolutionTheme';

interface VitalSigns {
  blood_pressure_systolic?: number;
  blood_pressure_diastolic?: number;
  heart_rate?: number;
  respiratory_rate?: number;
  temperature?: number;
  oxygen_saturation?: number;
  weight?: number;
  height?: number;
  bmi?: number;
  pain_scale?: number;
  blood_glucose?: number;
}

const emptyVitals: VitalSigns = {};

const emptyForm = () => ({
  patient_id: '',
  professional_id: '',
  appointment_id: '',
  evolution_date: new Date().toISOString().split('T')[0],
  evolution_time: new Date().toTimeString().slice(0, 5),
  evolution_entry_format: 'free_text' as const,
  evolution_free_text: '',
  subjective: '',
  objective: '',
  assessment: '',
  plan: '',
  vital_signs: { ...emptyVitals },
  diagnosis: '',
  diagnosis_code: '',
  procedures_performed: [] as any[],
  internal_notes: '',
  follow_up_notes: '',
  follow_up_date: '',
  metadata: {} as Record<string, unknown>,
  // CRM: derivado do desfecho no salvar (evolutionOutcomeModel)
  crm_outcome: undefined as any,
});

const toArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

export interface HealthcareEvolutionDialogProps {
  open: boolean;
  onClose: () => void;
  patient: {
    id: string;
    full_name: string;
    primary_diagnosis?: string | null;
    primary_diagnosis_code?: string | null;
  } | null;
  onSaved?: () => void;
  onError?: (message: string) => void;
  /** Quando definido (ex.: abrir pela timeline na agenda), carrega PUT em vez de POST. */
  existingEvolutionId?: string | null;
  /**
   * Painel inline (ex.: teleconsulta profissional): sem portal, sem backdrop, ocupa o container pai.
   */
  embedded?: boolean;
}

const HealthcareEvolutionDialog: React.FC<HealthcareEvolutionDialogProps> = ({
  open,
  onClose,
  patient,
  onSaved,
  onError,
  existingEvolutionId = null,
  embedded = false,
}) => {
  const { user } = useAuth();
  const { isFisioterapia } = useClinicMedicalSpecialty();
  const evolutionViewer = { role: user?.role, professional_id: user?.professional_id };
  // Profissionais logados ficam travados em si mesmos; perfis administrativos
  // (recepção/admin) podem escolher o profissional (sugestão médica 29.05).
  const canSelectProfessional = (user?.role as string) !== 'professional';
  const theme = useTheme();
  const { isMobile } = useClinicPageBreakpoints();
  const compactLayout = useEvolutionCompactLayout();
  const [consultAudioOpen, setConsultAudioOpen] = useState(!compactLayout);
  const [printablesSectionOpen, setPrintablesSectionOpen] = useState(true);
  const [indicationsSectionOpen, setIndicationsSectionOpen] = useState(true);

  const [form, setForm] = useState<any>(emptyForm());
  const [saving, setSaving] = useState(false);
  const finalizeInProgressRef = useRef(false);
  const [profSearch, setProfSearch] = useState('');
  const [profOptions, setProfOptions] = useState<any[]>([]);
  const lastProfRef = useRef('');

  const [printWizardOpen, setPrintWizardOpen] = useState(false);
  const [editPrintableTarget, setEditPrintableTarget] = useState<EvolutionAttachedPrintable | null>(null);
  const [indicationsDialogOpen, setIndicationsDialogOpen] = useState(false);
  const [attachedPrintables, setAttachedPrintables] = useState<EvolutionAttachedPrintable[]>([]);
  const [outcome, setOutcome] = useState<EvolutionOutcome>(() => defaultEvolutionOutcome());
  /** Liga os avisos de campo obrigatório só depois da primeira tentativa de concluir. */
  const [outcomeValidationOn, setOutcomeValidationOn] = useState(false);
  const [scheduleReturnPrompt, setScheduleReturnPrompt] = useState<{
    patientId: string;
    patientName?: string;
    professionalId?: string;
    suggestion: OutcomeSchedulingSuggestion;
  } | null>(null);
  const [indicationItems, setIndicationItems] = useState<EvolutionIndicationItem[]>([]);
  const [indicationSelections, setIndicationSelections] = useState<
    Record<string, { od: boolean; oe: boolean; ao: boolean }> | null
  >(null);
  const [procedureLabels, setProcedureLabels] = useState<string[]>([]);
  const [planExecutionRecords, setPlanExecutionRecords] = useState<EvolutionPlanExecutionRecord[]>([]);
  const [evolutionProcedureExecutionEnabled, setEvolutionProcedureExecutionEnabled] = useState(true);
  const [inventoryConsumption, setInventoryConsumption] = useState<EvolutionConsumptionLine[]>([]);
  const [inventoryAlreadyApplied, setInventoryAlreadyApplied] = useState(false);
  const [indicationPrintable, setIndicationPrintable] = useState<EvolutionAttachedPrintable | null>(null);
  const [autosaveEvolutionId, setAutosaveEvolutionId] = useState<string | null>(null);
  const [evolutionTemplates, setEvolutionTemplates] = useState<any[]>([]);
  const [evolutionTemplateManagerOpen, setEvolutionTemplateManagerOpen] = useState(false);

  const [loadingExisting, setLoadingExisting] = useState(false);
  const [loadedEvolutionSnapshot, setLoadedEvolutionSnapshot] = useState<any | null>(null);
  const [signing, setSigning] = useState(false);
  const isSignedState = Boolean(
    loadedEvolutionSnapshot && (loadedEvolutionSnapshot.status === 'signed' || loadedEvolutionSnapshot.is_signed),
  );
  const canEditLoadedEvolution =
    !existingEvolutionId ||
    (loadedEvolutionSnapshot && canEditEvolutionRecord(evolutionViewer, loadedEvolutionSnapshot));
  const contentDisabled = loadingExisting || Boolean(existingEvolutionId && loadedEvolutionSnapshot && !canEditLoadedEvolution);
  const loadedIsInvalidated = Boolean(loadedEvolutionSnapshot && isEvolutionInvalidated(loadedEvolutionSnapshot));

  const activeEvolutionId = existingEvolutionId || autosaveEvolutionId;
  // O id do rascunho chega no meio da digitação; trocar a key remontaria o editor e tiraria o foco.
  const [editorSessionKey, setEditorSessionKey] = useState('');

  useEffect(() => {
    if (open) setConsultAudioOpen(!compactLayout);
  }, [open, compactLayout]);

  const handleDraftEvolutionId = useCallback((id: string) => {
    if (!existingEvolutionId) setAutosaveEvolutionId(id);
  }, [existingEvolutionId]);

  const {
    status: draftSaveStatus,
    lastSavedAt: draftLastSavedAt,
    lastError: draftLastError,
    lastSaveFailed,
    flushAutosave,
    saveDraftWithOverrides,
  } = useEvolutionDraftAutosave({
    enabled: open && !saving && !loadingExisting && !contentDisabled && !loadedIsInvalidated,
    preserveStatus: isSignedState,
    evolutionId: activeEvolutionId,
    onEvolutionId: handleDraftEvolutionId,
    form,
    procedureLabels,
    indicationItems,
    planExecutionRecords,
    indicationSelections,
    attachedPrintables,
    indicationPrintable,
    outcome,
    userId: user?.id,
  });

  const draftStatusText = evolutionDraftStatusLabel(draftSaveStatus, draftLastSavedAt, isSignedState);

  const loadEvolutionTemplates = useCallback(async () => {
    try {
      const params: Record<string, string> = { category: 'evolution' };
      if (user?.professional_id) params.professional_id = user.professional_id;
      const { data } = await axios.get(getApiUrl('/clinic/document-text-templates'), { params });
      setEvolutionTemplates(data?.data || []);
    } catch {
      setEvolutionTemplates([]);
    }
  }, [user?.professional_id]);

  useEffect(() => {
    if (open) void loadEvolutionTemplates();
  }, [open, loadEvolutionTemplates]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await axios.get(getApiUrl('/clinic/settings'));
        const row = data?.data || data;
        if (!cancelled) {
          setEvolutionProcedureExecutionEnabled(row?.evolution_procedure_execution_enabled !== false);
        }
      } catch {
        if (!cancelled) setEvolutionProcedureExecutionEnabled(true);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!compactLayout) {
      setPrintablesSectionOpen(true);
      setIndicationsSectionOpen(true);
      return;
    }
    setPrintablesSectionOpen(attachedPrintables.length > 0);
    setIndicationsSectionOpen(procedureLabels.length > 0);
  }, [open, compactLayout, attachedPrintables.length, procedureLabels.length]);

  useEffect(() => {
    if (attachedPrintables.length > 0) setPrintablesSectionOpen(true);
  }, [attachedPrintables.length]);

  useEffect(() => {
    if (procedureLabels.length > 0) setIndicationsSectionOpen(true);
  }, [procedureLabels.length]);

  const handleClose = async () => {
    if (typeof document !== 'undefined') {
      (document.activeElement as HTMLElement | null)?.blur?.();
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
    await flushAutosave();
    if (
      lastSaveFailed() &&
      !window.confirm('As últimas alterações desta evolução não foram salvas. Fechar mesmo assim e perdê-las?')
    ) {
      return;
    }
    setAutosaveEvolutionId(null);
    onClose();
  };

  const [aiSnackbar, setAiSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' });

  const applyEvolutionTemplate = (template: any) => {
    setForm((prev: any) => ({
      ...prev,
      evolution_free_text: sanitizeClinicalHtml(template?.body_text) || prev.evolution_free_text,
    }));
    setAiSnackbar({
      open: true,
      message: `Modelo "${template?.name || 'Evolução'}" aplicado`,
      severity: 'success',
    });
  };

  const searchProfessionals = async (q: string) => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/prescriptions-search/professionals'), { params: { search: q } });
      const rows = toArray(data?.data ?? data) as any[];
      setProfOptions(mergeLoggedInProfessionalIntoOptions(rows, user));
    } catch {
      const me = getLoggedInProfessionalOption(user);
      setProfOptions(me ? [me] : []);
    }
  };

  useEffect(() => {
    if (!open || !patient?.id) return;

    setEditorSessionKey(existingEvolutionId || `new-${patient.id}-${Date.now()}`);

    if (!existingEvolutionId) {
      const me = getLoggedInProfessionalOption(user);
      setForm({
        ...emptyForm(),
        patient_id: patient.id,
        ...(me ? { professional_id: me.id } : {}),
      });
      setProfSearch(me?.full_name || '');
      searchProfessionals('');
      setAttachedPrintables([]);
      setIndicationPrintable(null);
      setAutosaveEvolutionId(null);
      setOutcome(defaultEvolutionOutcome());
      setOutcomeValidationOn(false);
      setProcedureLabels([]);
      setPlanExecutionRecords([]);
      setIndicationItems([]);
      setIndicationSelections(null);
      setInventoryConsumption([]);
      setInventoryAlreadyApplied(false);
      setLoadedEvolutionSnapshot(null);
      setLoadingExisting(false);
      return;
    }

    let cancelled = false;
    setLoadingExisting(true);
    setLoadedEvolutionSnapshot(null);
    void (async () => {
      try {
        const { data: evo } = await axios.get(getApiUrl(`/clinic/evolutions/${existingEvolutionId}`));
        if (cancelled) return;
        if (evo.patient_id && evo.patient_id !== patient.id) {
          onError?.('Esta evolução pertence a outro paciente.');
          setLoadingExisting(false);
          return;
        }
        const mergedFreeText = (() => {
          const existing = String(evo.evolution_free_text ?? '').trim();
          if (existing) return evo.evolution_free_text ?? '';
          if (resolveEvolutionEntryFormat(evo) === 'free_text') return evo.evolution_free_text ?? '';
          const parts: string[] = [];
          if (evo.subjective?.trim()) parts.push(`Subjetivo\n${evo.subjective.trim()}`);
          if (evo.objective?.trim()) parts.push(`Objetivo\n${evo.objective.trim()}`);
          if (evo.assessment?.trim()) parts.push(`Avaliação\n${evo.assessment.trim()}`);
          if (evo.plan?.trim()) parts.push(`Plano\n${evo.plan.trim()}`);
          return parts.join('\n\n');
        })();

        const evoColumns: Record<string, any> = { ...(evo || {}) };
        for (const key of ['additions', 'documents', 'images', 'patient', 'professional', 'appointment', 'unit']) {
          delete evoColumns[key];
        }
        setForm({
          ...emptyForm(),
          ...evoColumns,
          evolution_entry_format: 'free_text',
          evolution_free_text: prepareEvolutionFreeTextForEditor(mergedFreeText),
          subjective: '',
          objective: '',
          assessment: '',
          plan: '',
          vital_signs: evo.vital_signs || { ...emptyVitals },
          evolution_date: evo.evolution_date?.split('T')[0] || '',
          follow_up_date: evo.follow_up_date?.split('T')[0] || '',
          appointment_id: '',
          patient_id: patient.id,
        });

        const wf = evo.metadata?.evolution_workflow;
        const { printables, indication } = splitEvolutionWorkflowAssets(wf || {});
        setAttachedPrintables(printables);
        setIndicationPrintable(indication);
        // Evolução salva antes do desfecho automático só tem o painel "Retorno"
        // antigo: o adapter garante que ela reabra com a mesma informação.
        setOutcome(parseSavedOutcome(wf?.outcome) || fromLegacyReturnWorkflow(wf?.return));
        setOutcomeValidationOn(false);
        setIndicationSelections(wf?.indication_selections || null);
        const { planExecutions, indications } = splitEvolutionProceduresAndPlanExecutions(evo.procedures_performed);
        setPlanExecutionRecords(planExecutions);
        setIndicationItems(indications);
        setProcedureLabels(indications.map((i) => i.name));

        const consumed = Array.isArray(evo.consumed_materials) ? evo.consumed_materials : [];
        setInventoryConsumption(consumed.map((c: any) => ({
          item_id: c.item_id,
          batch_id: c.batch_id || null,
          quantity: Number(c.quantity) || 1,
          reservation_id: c.reservation_id || null,
          procedure_id: c.procedure_id || null,
          laterality: c.laterality || null,
          item_name: c.item_name || null,
          unit: c.unit || null,
          source: c.source || undefined,
        })));
        setInventoryAlreadyApplied(Array.isArray(evo.stock_movement_ids) && evo.stock_movement_ids.length > 0);

        if (evo.professional?.id) {
          setProfOptions(mergeLoggedInProfessionalIntoOptions([evo.professional], user));
          setProfSearch(evo.professional.full_name || '');
        } else {
          await searchProfessionals('');
          setProfSearch('');
        }

        setLoadedEvolutionSnapshot(evo);
      } catch (e: any) {
        console.error(e);
        onError?.(e?.response?.data?.error || 'Erro ao carregar evolução');
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, patient?.id, existingEvolutionId]);

  useEffect(() => {
    if (!open) return;
    if (profSearch === lastProfRef.current) return;
    const t = setTimeout(() => {
      lastProfRef.current = profSearch;
      searchProfessionals(profSearch);
    }, 300);
    return () => clearTimeout(t);
  }, [profSearch, open]);

  const updateVital = (field: string, value: string) => {
    const num = value === '' ? undefined : parseFloat(value);
    setForm((prev: any) => ({ ...prev, vital_signs: { ...prev.vital_signs, [field]: num } }));
  };

  const printableCategoryLabel: Record<EvolutionPrintableCategory, string> = {
    prescription: 'Receituário',
    certificate: 'Atestado',
    exam_order: 'Exames',
    refraction: 'Refração',
    indication: 'Indicações',
    medical_report: 'Relatório médico',
    report: 'Laudo',
    surgical_risk: 'Risco cirúrgico',
    insurance_guide: 'Guia de convênio',
  };

  const printHtmlInNewWindow = (html: string) => {
    printClinicHtml(html);
  };

  /** Paciente completo para impressos (indicações usam bloco detalhado estilo atestado). */
  const buildPrintPatientCtx = (): any | null => {
    const snap = loadedEvolutionSnapshot?.patient;
    const merged = snap || patient;
    if (!merged?.full_name) return null;
    return merged;
  };

  const extractBodyInner = (fullHtml: string) => {
    const m = fullHtml.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    return m ? m[1].trim() : fullHtml;
  };

  const updateAttachedPrintable = useCallback((updated: EvolutionAttachedPrintable) => {
    setAttachedPrintables((prev) => {
      const next = prev.map((p) => (p.clientKey === updated.clientKey ? updated : p));
      void saveDraftWithOverrides({ attachedPrintables: next });
      return next;
    });
  }, [saveDraftWithOverrides]);

  const printAttachedDocuments = async (items: EvolutionAttachedPrintable[]) => {
    if (!items.length) return;
    let clinic: any = null;
    try {
      const { data } = await axios.get(getApiUrl('/clinic/settings'));
      clinic = data?.data ?? null;
    } catch {
      /* ignore */
    }

    // Profissional selecionado na evolução (para assinatura nos docs de texto livre)
    const selectedProf = profOptions.find((o: any) => o.id === form.professional_id) ?? null;
    const patientCtx = buildPrintPatientCtx();
    const buildTextDocHtml = (p: EvolutionAttachedPrintable) =>
      buildEvolutionTextDocBodyHtml({
        category: p.category,
        templateName: p.templateName,
        bodyText: String(p.bodyText || ''),
        patient: patientCtx,
        professional: selectedProf,
        issueDate: form.evolution_date,
      });

    const parts: string[] = [];
    const signedPrescriptions: any[] = [];
    const signedMedicalReports: any[] = [];
    for (const p of items) {
      if (p.linkedDocumentId && p.linkedDocumentType === 'prescription') {
        const resolved = await resolvePrescriptionPrintPart(p.linkedDocumentId, extractBodyInner);
        if (resolved?.kind === 'signed') {
          signedPrescriptions.push(resolved.prescription);
          parts.push(buildSignedPrescriptionNoticeHtml(resolved.prescription));
        } else if (resolved?.kind === 'html') {
          parts.push(resolved.html);
        } else {
          parts.push(buildTextDocHtml(p));
        }
      } else if (p.linkedDocumentId && p.linkedDocumentType === 'certificate') {
        try {
          const { data } = await axios.get(getApiUrl(`/clinic/certificates/${p.linkedDocumentId}/print`));
          const printData = data?.data ?? data;
          parts.push(
            `<div class="attachment-block attachment-block--embedded">${extractBodyInner(buildCertificatePrintHtml(printData))}</div>`,
          );
        } catch (e) {
          parts.push(isCidConsentRequiredError(e) ? buildCidConsentBlockedNoticeHtml() : buildTextDocHtml(p));
        }
      } else if (p.linkedDocumentId && p.linkedDocumentType === 'medical_report') {
        const resolved = await resolveMedicalReportPrintPart(p.linkedDocumentId, extractBodyInner);
        if (resolved?.kind === 'signed') {
          signedMedicalReports.push(resolved.report);
          parts.push(buildSignedMedicalReportNoticeHtml(resolved.report));
        } else if (resolved?.kind === 'html') {
          parts.push(resolved.html);
        } else {
          parts.push(buildTextDocHtml(p));
        }
      } else if (p.linkedDocumentId && p.linkedDocumentType === 'exam_order') {
        try {
          const { data } = await axios.get(getApiUrl(`/clinic/exam-orders/${p.linkedDocumentId}/print`));
          const printData = data?.data ?? data;
          parts.push(
            `<div class="attachment-block attachment-block--embedded">${extractBodyInner(buildExamOrderPrintHtml(printData))}</div>`,
          );
        } catch {
          parts.push(buildTextDocHtml(p));
        }
      } else {
        // Documentos de texto livre: exam_order, refraction, report, surgical_risk,
        // insurance_guide, indication — renderiza com timbrado estruturado completo.
        parts.push(buildTextDocHtml(p));
      }
    }

    const sheets = parts.map((bodyHtml) => ({ bodyHtml }));
    printHtmlInNewWindow(
      buildClinicMultiSheetPrintHtml({
        clinic,
        browserTitle: 'Impressão',
        sheets,
      }),
    );
    for (const rx of signedPrescriptions) {
      await openSignedPrescriptionPdf(rx.id, rx.signed_pdf_url).catch(() => undefined);
    }
    for (const report of signedMedicalReports) {
      await openSignedMedicalReportPdf(report.id, report.signed_pdf_url).catch(() => undefined);
    }
  };

  const printProcedureLabel = async (name: string) => {
    let clinic: any = null;
    try {
      const { data } = await axios.get(getApiUrl('/clinic/settings'));
      clinic = data?.data ?? null;
    } catch {
      /* ignore */
    }
    const pctx = buildPrintPatientCtx();
    const section =
      buildClinicalPrintPatientInfoHtml(pctx, { variant: 'detailed', issueDate: new Date() }) +
      buildEvolutionPrintAttachmentSection(
        'Indicações',
        'Procedimento indicado',
        `<p style="margin:0">${escapeHtml(name)}</p>`,
      );
    printHtmlInNewWindow(
      buildClinicPrintDocumentHtml({
        clinic,
        browserTitle: name.slice(0, 120),
        badgeText: 'Indicação',
        bodyHtml: section,
      }),
    );
  };

  const removeProcedureLabel = (label: string) => {
    setProcedureLabels((prev) => {
      const next = prev.filter((x) => x !== label);
      if (next.length === 0) setIndicationPrintable(null);
      return next;
    });
    setIndicationItems((prev) => prev.filter((i) => i.name !== label));
  };

  const handleSignExisting = async () => {
    if (!existingEvolutionId) return;
    if (!window.confirm('Deseja assinar esta evolução? Administradores poderão excluí-la por até 24 horas após a assinatura.')) return;
    try {
      setSigning(true);
      await axios.patch(getApiUrl(`/clinic/evolutions/${existingEvolutionId}/sign`));
      const { data: fresh } = await axios.get(getApiUrl(`/clinic/evolutions/${existingEvolutionId}`));
      setLoadedEvolutionSnapshot(fresh);
      setForm((prev: any) => ({
        ...prev,
        ...fresh,
        evolution_entry_format: 'free_text',
        evolution_date: fresh.evolution_date?.split('T')[0] || prev.evolution_date,
        follow_up_date: fresh.follow_up_date?.split?.('T')?.[0] ?? prev.follow_up_date ?? '',
      }));
      setAiSnackbar({ open: true, message: 'Evolução assinada com sucesso!', severity: 'success' });
      onSaved?.();
    } catch (err: any) {
      console.error(err);
      onError?.(err?.response?.data?.error || 'Erro ao assinar evolução');
    } finally {
      setSigning(false);
    }
  };

  const handleSave = async () => {
    if (finalizeInProgressRef.current) return;

    if (!form.patient_id || !form.professional_id) {
      onError?.('Selecione paciente e profissional.');
      return;
    }

    // "Concluir" sempre promove rascunho → assinada e dispara cards de CRM.
    const finalizing = true;
    if (finalizing) {
      const detection = detectOutcomeScenario({ indicationItems, printables: attachedPrintables });
      const pending = validateOutcome(resolveEffectiveOutcome(outcome, detection), detection);
      if (pending) {
        setOutcomeValidationOn(true);
        onError?.(pending);
        return;
      }
      const tracePending = validateTraceabilityLines(
        inventoryConsumption.filter((l) => l.item_id && l.quantity > 0),
      );
      if (tracePending) {
        onError?.(tracePending);
        return;
      }
    }

    finalizeInProgressRef.current = true;
    setSaving(true);
    try {
      const flushedId = await flushAutosave({ waitOnly: true });
      const targetId = flushedId || existingEvolutionId || autosaveEvolutionId;
      const payload = buildEvolutionSavePayload({
        form,
        procedureLabels,
        indicationItems,
        indicationSelections,
        attachedPrintables,
        indicationPrintable,
        outcome,
        isCreate: !targetId,
        attachCrm: finalizing,
        userId: user?.id,
      });

      let savedId: string | null = targetId || null;

      if (targetId) {
        await axios.put(getApiUrl(`/clinic/evolutions/${targetId}`), payload);
        const { data: fresh } = await axios.get(getApiUrl(`/clinic/evolutions/${targetId}`));
        setLoadedEvolutionSnapshot(fresh);
        savedId = targetId;
      } else {
        const postRes = await axios.post(getApiUrl('/clinic/evolutions'), payload);
        savedId = extractEvolutionIdFromResponse(postRes);
      }

      if (finalizing && savedId && !inventoryAlreadyApplied) {
        try {
          const applyRes = await axios.post(getApiUrl('/inventory-module/evolution-consumption/apply'), {
            evolution_id: savedId,
            patient_id: form.patient_id,
            professional_id: form.professional_id,
            lines: inventoryConsumption.filter((l) => l.item_id && l.quantity > 0),
          });
          const warnings: string[] = applyRes.data?.data?.stock_warnings || [];
          if (warnings.length) {
            setAiSnackbar({
              open: true,
              message: `Evolução concluída. Avisos de estoque: ${warnings.join('; ')}`,
              severity: 'warning',
            });
          }
          if (applyRes.data?.data?.applied || applyRes.data?.data?.already_applied) {
            setInventoryAlreadyApplied(true);
          }
        } catch (invErr: any) {
          setAiSnackbar({
            open: true,
            message: invErr.response?.data?.error || 'Evolução salva, mas falha ao baixar estoque',
            severity: 'warning',
          });
        }
      }

      onSaved?.();
      const detection = detectOutcomeScenario({ indicationItems, printables: attachedPrintables });
      const suggestion = getOutcomeSchedulingSuggestion(outcome, detection);
      if (suggestion && form.patient_id) {
        setScheduleReturnPrompt({
          patientId: form.patient_id,
          patientName: patient?.full_name,
          professionalId: form.professional_id,
          suggestion,
        });
        return;
      }

      if (!embedded) {
        setAutosaveEvolutionId(null);
        onClose();
      } else {
        setAiSnackbar({
          open: true,
          message: 'Evolução salva no prontuário. Você pode continuar na chamada.',
          severity: 'success',
        });
      }
    } catch (err: any) {
      console.error(err);
      onError?.(err?.response?.data?.error || 'Erro ao salvar evolução');
    } finally {
      setSaving(false);
      finalizeInProgressRef.current = false;
    }
  };

  if (!patient) return null;

  const getPrintSnapshot = () =>
    buildEvolutionPrintSnapshot({
      form,
      patient: loadedEvolutionSnapshot?.patient || patient,
      professional:
        profOptions.find((p: any) => p.id === form.professional_id) ||
        loadedEvolutionSnapshot?.professional ||
        getLoggedInProfessionalOption(user),
      procedureLabels,
      attachedPrintables,
      indicationPrintable,
      // O impresso continua no formato antigo de "Retorno".
      returnWorkflow: toLegacyReturnWorkflow(outcome),
    });

  const printEvolutionComplete = async () => {
    await printClinicalEvolutionFullDocument(getPrintSnapshot());
  };

  const printEvolutionBatch = async () => {
    await printClinicalEvolutionBatchDocument(getPrintSnapshot());
  };

  const initialProfessional =
    profOptions.find((p: any) => p.id === form.professional_id) ||
    getLoggedInProfessionalOption(user);

  return (
    <>
      <Dialog
        open={open && !scheduleReturnPrompt}
        onClose={() => void handleClose()}
        maxWidth={false}
        fullWidth={!embedded}
        fullScreen={embedded ? false : isMobile}
        disablePortal={embedded}
        hideBackdrop={embedded}
        disableEscapeKeyDown={embedded}
        scroll="paper"
        PaperProps={{
          sx: embedded
            ? evolutionDialogPaperSx(isMobile, true, compactLayout)
            : isMobile
              ? mobileFullScreenDialogPaperSx
              : evolutionDialogPaperSx(isMobile, false, compactLayout),
        }}
        sx={
          embedded
            ? {
                position: 'static',
                flex: 1,
                alignSelf: 'stretch',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                '& .MuiDialog-container': {
                  height: '100%',
                  alignItems: 'stretch',
                  justifyContent: 'stretch',
                },
              }
            : (isMobile ? undefined : clinicFormDialogRootSx)
        }
      >
        <DialogContent
          className={isMobile ? MOBILE_FORM_SURFACE_CLASS : undefined}
          sx={evolutionDialogContentSxFor(compactLayout)}
        >
          {loadingExisting ? (
            <Box sx={evolutionLoadingSkeletonSx}>
              <Stack spacing={1.5}>
                <Skeleton variant="rounded" height={56} />
                <Skeleton variant="rounded" height={180} />
                <Skeleton variant="rounded" height={120} />
                <Skeleton variant="rounded" height={96} />
              </Stack>
            </Box>
          ) : (
            <>
          <EvolutionAttendanceIdentificationSection
            layout="panel"
            compact={compactLayout}
            professionalField={
              canSelectProfessional ? (
                <Autocomplete
                  size="small"
                  disabled={contentDisabled}
                  options={profOptions}
                  getOptionLabel={(o: any) => `${o.full_name || ''}${o.specialty ? ` - ${o.specialty}` : ''}`}
                  onInputChange={(_, v, r) => {
                    if (r !== 'reset') setProfSearch(v);
                  }}
                  onChange={(_, v) => setForm({ ...form, professional_id: v?.id || '' })}
                  value={profOptions.find((p: any) => p.id === form.professional_id) || null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={undefined}
                      placeholder="Profissional *"
                      inputProps={{ ...params.inputProps, 'aria-label': 'Profissional' }}
                    />
                  )}
                  isOptionEqualToValue={(o: any, v: any) => o.id === v.id}
                  noOptionsText="Nenhum profissional encontrado"
                />
              ) : undefined
            }
            diagnosisField={
              <TextField
                fullWidth
                size="small"
                disabled={contentDisabled}
                label="Diagnóstico"
                value={form.diagnosis}
                onChange={(e) => setForm({ ...form, diagnosis: e.target.value })}
                placeholder="Ex.: cardiomiopatia dilatada"
              />
            }
            cidField={
              <Cid10Autocomplete
                label="CID-10"
                disabled={contentDisabled}
                value={form.diagnosis_code}
                description={form.diagnosis}
                onChange={(code, desc) =>
                  setForm((prev: any) => ({
                    ...prev,
                    diagnosis_code: code,
                    diagnosis: prev.diagnosis?.trim() ? prev.diagnosis : desc,
                  }))
                }
              />
            }
          />

          <Paper elevation={0} sx={evolutionSectionPaperSxFor(compactLayout)}>
            <Box
              sx={{
                ...evolutionSectionHeaderBarSxFor(compactLayout),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
                py: 0.85,
              }}
            >
              <Typography variant="overline" sx={evolutionSectionTitleSx}>
                Evolução
              </Typography>
              {!contentDisabled && !loadedIsInvalidated && existingEvolutionId && loadedEvolutionSnapshot && !isSignedState && (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  disabled={signing || loadingExisting}
                  onClick={() => void handleSignExisting()}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12 }}
                >
                  {signing ? 'Assinando…' : 'Assinar'}
                </Button>
              )}
            </Box>
            <Box sx={evolutionSectionBodySx(compactLayout)}>
              {!contentDisabled && compactLayout && (
                <Button
                  size="small"
                  onClick={() => setConsultAudioOpen((prev) => !prev)}
                  sx={{ ...evolutionLinkButtonSx, mb: 0.5, fontSize: '0.75rem' }}
                >
                  {consultAudioOpen ? 'Ocultar captura de áudio' : 'Captura de áudio do consultório (opcional)'}
                </Button>
              )}
              <Collapse in={!compactLayout || consultAudioOpen} unmountOnExit={compactLayout}>
                {!contentDisabled && (
                  <EvolutionConsultAudioPanel
                    compact={compactLayout}
                    disabled={contentDisabled}
                    patientName={patient.full_name}
                    professionalName={
                      profOptions.find((p: any) => p.id === form.professional_id)?.full_name ||
                      loadedEvolutionSnapshot?.professional?.full_name ||
                      getLoggedInProfessionalOption(user)?.full_name ||
                      ''
                    }
                    medicalHistory={[
                      form.diagnosis?.trim() && `Diagnóstico informado: ${form.diagnosis.trim()}`,
                      form.internal_notes?.trim() && `Notas: ${form.internal_notes.trim()}`,
                    ]
                      .filter(Boolean)
                      .join('\n')}
                    onApply={(payload) => {
                      setForm((prev: any) => ({
                        ...prev,
                        evolution_entry_format: 'free_text',
                        evolution_free_text: prepareEvolutionFreeTextForEditor(payload.evolution_free_text),
                        subjective: '',
                        objective: '',
                        assessment: '',
                        plan: '',
                        diagnosis: payload.diagnosis?.trim() ? payload.diagnosis : prev.diagnosis,
                        diagnosis_code: payload.diagnosis_code?.trim()
                          ? payload.diagnosis_code
                          : prev.diagnosis_code,
                        internal_notes: payload.internal_notes?.trim()
                          ? payload.internal_notes
                          : prev.internal_notes,
                        follow_up_notes: payload.follow_up_notes?.trim()
                          ? payload.follow_up_notes
                          : prev.follow_up_notes,
                      }));
                      setAiSnackbar({
                        open: true,
                        message: 'Evolução gerada a partir do áudio. Revise antes de concluir.',
                        severity: 'success',
                      });
                    }}
                    onError={(msg) => onError?.(msg)}
                  />
                )}
              </Collapse>
              {!existingEvolutionId && !contentDisabled && (
                evolutionTemplates.length > 0 ? (
                  <PrescriptionTemplateChipPicker
                    dense={compactLayout}
                    templates={evolutionTemplates}
                    onSelect={applyEvolutionTemplate}
                    onReorder={async (ids) => {
                      await axios.put(getApiUrl('/clinic/document-text-templates/reorder'), { ids });
                      await loadEvolutionTemplates();
                    }}
                    title="Modelos de evolução"
                    searchPlaceholder="Pesquisar modelo..."
                    footerExtra={(
                      <Button size="small" onClick={() => setEvolutionTemplateManagerOpen(true)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                        Gerenciar modelos
                      </Button>
                    )}
                  />
                ) : (
                  <Alert severity="info" sx={{ mb: compactLayout ? 0.75 : 2, py: compactLayout ? 0 : undefined }} action={(
                    <Button color="inherit" size="small" onClick={() => setEvolutionTemplateManagerOpen(true)} sx={{ textTransform: 'none', fontWeight: 700 }}>
                      Criar modelo
                    </Button>
                  )}>
                    Nenhum modelo pré-preenchido. Cadastre textos padrão para agilizar evoluções.
                  </Alert>
                )
              )}
              <ClinicalHtmlEditor
                key={editorSessionKey}
                contentKey={editorSessionKey}
                label="Texto da evolução"
                dense={compactLayout}
                minRows={compactLayout ? 5 : 10}
                disabled={contentDisabled}
                value={form.evolution_free_text}
                onChange={(html) => setForm({ ...form, evolution_free_text: html })}
                placeholder="Evolução em texto livre..."
              />
              {evolutionProcedureExecutionEnabled && patient?.id && !contentDisabled && (
                <Paper elevation={0} sx={{ ...evolutionSectionPaperSxFor(compactLayout), mt: compactLayout ? 1 : 2, p: 0, overflow: 'hidden' }}>
                  <EvolutionProcedureExecutionPanel
                    patientId={patient.id}
                    disabled={contentDisabled}
                    compact={compactLayout}
                    evolutionId={activeEvolutionId}
                    defaultProfessionalId={form.professional_id || user?.professional_id || ''}
                    executedInSession={planExecutionRecords}
                    professionalOptions={profOptions}
                    onNeedProfessionals={() => { if (profOptions.length === 0) void searchProfessionals(''); }}
                    onError={(message) => setAiSnackbar({ open: true, message, severity: 'error' })}
                    onSuccess={(message) => setAiSnackbar({ open: true, message, severity: 'success' })}
                    onExecuted={(record) => {
                      const nextRecords = [...planExecutionRecords, record];
                      const nextFreeText = appendPlanExecutionToFreeText(form.evolution_free_text, record);
                      const nextForm = { ...form, evolution_free_text: nextFreeText };
                      setPlanExecutionRecords(nextRecords);
                      setForm(nextForm);
                      void saveDraftWithOverrides(
                        { form: nextForm, planExecutionRecords: nextRecords },
                        { force: true },
                      );
                      onSaved?.();
                    }}
                  />
                </Paper>
              )}
            </Box>
          </Paper>

          {activeEvolutionId && isSignedState && !isEvolutionInvalidated(loadedEvolutionSnapshot) && (
            <EvolutionAdditionsSection
              evolutionId={activeEvolutionId}
              evolutionSnapshot={loadedEvolutionSnapshot}
              patientId={form.patient_id || patient?.id}
              viewer={evolutionViewer}
              professionalId={user?.professional_id || form.professional_id || null}
              onError={(message) => setAiSnackbar({ open: true, message, severity: 'error' })}
            />
          )}

          {!isFisioterapia && (
          <Paper elevation={0} sx={evolutionSectionPaperSxFor(compactLayout)}>
            <Box
              onClick={() => {
                if (contentDisabled) return;
                setPrintWizardOpen(true);
              }}
              onKeyDown={(e) => {
                if (contentDisabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPrintWizardOpen(true);
                }
              }}
              role="button"
              tabIndex={contentDisabled ? -1 : 0}
              sx={evolutionClickableRowSxFor(contentDisabled, compactLayout)}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ ...evolutionSectionHeadingSx, fontSize: compactLayout ? '0.72rem' : undefined }}>
                  IMPRESSOS E SOLICITAÇÕES
                  {compactLayout && attachedPrintables.length > 0 ? (
                    <Typography component="span" variant="caption" sx={{ ml: 0.75, fontWeight: 600, color: '#64748b' }}>
                      ({attachedPrintables.length})
                    </Typography>
                  ) : null}
                </Typography>
                {!compactLayout ? (
                  <Typography variant="caption" sx={evolutionCaptionMutedSx}>
                    Receituário, atestado, pedido de exame, laudo ou risco cirúrgico (para cirurgias e procedimentos, use a área Indicações logo abaixo)
                  </Typography>
                ) : (
                  <Tooltip
                    title="Receituário, atestado, pedido de exame, laudo ou risco cirúrgico. Cirurgias/procedimentos ficam em Indicações."
                    placement="top"
                  >
                    <Typography variant="caption" sx={{ ...evolutionCaptionMutedSx, display: 'block' }}>
                      Receituário, atestado, exames, laudo…
                    </Typography>
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
                {compactLayout && attachedPrintables.length === 0 && (
                  <IconButton
                    size="small"
                    aria-label={printablesSectionOpen ? 'Recolher seção' : 'Expandir seção'}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPrintablesSectionOpen((v) => !v);
                    }}
                  >
                    <ExpandMoreAccordionIcon
                      sx={{
                        fontSize: 20,
                        transform: printablesSectionOpen ? 'rotate(180deg)' : undefined,
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                )}
                <EvolutionPrescriptionsSendButton
                  printables={attachedPrintables}
                  patientId={form.patient_id}
                  onNotify={(message, severity) => setAiSnackbar({ open: true, message, severity })}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<NoteAddIcon />}
                  disabled={contentDisabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (contentDisabled) return;
                    setPrintWizardOpen(true);
                  }}
                  sx={{ ...evolutionSecondaryOutlinedButtonSx, flexShrink: 0 }}
                >
                  Adicionar
                </Button>
              </Box>
            </Box>
            <Collapse in={!compactLayout || printablesSectionOpen || attachedPrintables.length > 0} unmountOnExit={compactLayout}>
            <Box sx={evolutionSectionBodySx(compactLayout)}>
              {!compactLayout && (
                <Typography variant="caption" sx={{ ...evolutionCaptionMutedSx, fontWeight: 600 }}>
                  Materiais selecionados
                </Typography>
              )}
              <Box sx={evolutionListDropzoneSx(compactLayout)}>
                {attachedPrintables.length === 0 ? (
                  <Typography variant="body2" sx={{ ...evolutionEmptyStateSx, fontSize: compactLayout ? '0.8rem' : undefined }}>
                    {compactLayout
                      ? 'Nenhum impresso. Toque em Adicionar no cabeçalho.'
                      : 'Nenhum documento anexado. Use o cabeçalho acima para lançar modelos cadastrados na clínica.'}
                  </Typography>
                ) : (
                  attachedPrintables.map((p) => (
                    <EvolutionAttachedPrintableChip
                      key={p.clientKey}
                      printable={p}
                      categoryLabels={printableCategoryLabel}
                      disabled={contentDisabled}
                      patientId={form.patient_id}
                      variant="evolution"
                      onEdit={setEditPrintableTarget}
                      onPrint={(item) => void printAttachedDocuments([item])}
                      onRemove={(item) =>
                        setAttachedPrintables((prev) => prev.filter((x) => x.clientKey !== item.clientKey))
                      }
                      onPrescriptionError={(message) => onError?.(message)}
                      onPrescriptionSigned={(info) =>
                        setAiSnackbar({
                          open: true,
                          message: `Receituário assinado. Código: ${info?.signature_verification_code || '—'}`,
                          severity: 'success',
                        })
                      }
                    />
                  ))
                )}
              </Box>
            </Box>
            </Collapse>
          </Paper>
          )}

          <EvolutionFileAttachmentsSection
            compact={compactLayout}
            patientId={form.patient_id}
            evolutionId={activeEvolutionId}
            professionalId={form.professional_id}
            disabled={contentDisabled}
            onEnsureEvolutionId={() => flushAutosave({ force: true })}
            onNotify={(message, severity) =>
              setAiSnackbar({ open: true, message, severity: severity || 'success' })
            }
          />

          <Paper elevation={0} sx={evolutionSectionPaperSxFor(compactLayout)}>
            <Box
              onClick={() => {
                if (contentDisabled) return;
                setIndicationsDialogOpen(true);
              }}
              onKeyDown={(e) => {
                if (contentDisabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIndicationsDialogOpen(true);
                }
              }}
              role="button"
              tabIndex={contentDisabled ? -1 : 0}
              sx={evolutionClickableRowSxFor(contentDisabled, compactLayout)}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ ...evolutionSectionHeadingSx, fontSize: compactLayout ? '0.72rem' : undefined }}>
                  INDICAÇÕES
                  {compactLayout && procedureLabels.length > 0 ? (
                    <Typography component="span" variant="caption" sx={{ ml: 0.75, fontWeight: 600, color: '#64748b' }}>
                      ({procedureLabels.length})
                    </Typography>
                  ) : null}
                </Typography>
                {!compactLayout ? (
                  <Typography variant="caption" sx={evolutionCaptionMutedSx}>
                    Cirurgias e procedimentos. Use Adicionar para abrir o seletor (cadastro ou grade OD/OE/AO em oftalmologia).
                  </Typography>
                ) : (
                  <Tooltip title="Cirurgias e procedimentos — seletor com grade OD/OE/AO em oftalmologia." placement="top">
                    <Typography variant="caption" sx={evolutionCaptionMutedSx}>
                      Cirurgias e procedimentos
                    </Typography>
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                {compactLayout && procedureLabels.length === 0 && (
                  <IconButton
                    size="small"
                    aria-label={indicationsSectionOpen ? 'Recolher seção' : 'Expandir seção'}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIndicationsSectionOpen((v) => !v);
                    }}
                  >
                    <ExpandMoreAccordionIcon
                      sx={{
                        fontSize: 20,
                        transform: indicationsSectionOpen ? 'rotate(180deg)' : undefined,
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                )}
              <Button
                size="small"
                variant="outlined"
                startIcon={<NoteAddIcon />}
                disabled={contentDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (contentDisabled) return;
                  setIndicationsDialogOpen(true);
                }}
                sx={{ ...evolutionSecondaryOutlinedButtonSx, flexShrink: 0 }}
              >
                Adicionar
              </Button>
              </Box>
            </Box>
            <Collapse in={!compactLayout || indicationsSectionOpen || procedureLabels.length > 0} unmountOnExit={compactLayout}>
            <Box sx={evolutionSectionBodySx(compactLayout)}>
              {!compactLayout && (
                <Typography variant="caption" sx={{ ...evolutionCaptionMutedSx, fontWeight: 600 }}>
                  Cirurgias e procedimentos selecionados
                </Typography>
              )}
              <Box sx={{ ...evolutionListDropzoneSx(compactLayout), maxHeight: compactLayout ? 140 : 160 }}>
                {procedureLabels.length === 0 ? (
                  <Typography variant="body2" sx={{ ...evolutionEmptyStateSx, fontSize: compactLayout ? '0.8rem' : undefined }}>
                    Nenhuma indicação registrada.
                  </Typography>
                ) : (
                  procedureLabels.map((label, idx) => (
                    <Paper
                      key={`proc-${idx}-${label}`}
                      variant="outlined"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        pl: 1.25,
                        pr: 0.25,
                        py: 0.25,
                        borderRadius: 10,
                        borderColor: EVOLUTION_THEME.borderSoft,
                      }}
                    >
                      <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 180, sm: 280 }, fontWeight: 600 }}>
                        {label}
                      </Typography>
                      <Tooltip title="Imprimir indicação">
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (indicationPrintable?.bodyText?.trim()) {
                              void printAttachedDocuments([indicationPrintable]);
                            } else {
                              void printProcedureLabel(label);
                            }
                          }}
                          aria-label="Imprimir"
                        >
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!contentDisabled && (
                        <Tooltip title="Remover">
                          <IconButton
                            size="small"
                            onClick={() => removeProcedureLabel(label)}
                            aria-label="Remover"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Paper>
                  ))
                )}
              </Box>
              {Boolean(String(indicationPrintable?.bodyText || '').trim()) && (
                <Box sx={{ mt: compactLayout ? 1 : 2 }}>
                  <Typography variant="caption" sx={{ ...evolutionCaptionMutedSx, fontWeight: 600 }}>
                    Detalhes e observações
                  </Typography>
                  <Typography component="pre" variant="body2" sx={evolutionMonospaceBlockSx}>
                    {indicationPrintable?.bodyText}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {!contentDisabled && indicationPrintable ? (
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => setEditPrintableTarget(indicationPrintable)}
                        sx={evolutionLinkButtonSx}
                      >
                        Editar indicações
                      </Button>
                    ) : null}
                    <Button
                      size="small"
                      startIcon={<PrintIcon />}
                      onClick={() => void printAttachedDocuments([indicationPrintable!])}
                      sx={evolutionLinkButtonSx}
                    >
                      Imprimir indicações completas
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
            </Collapse>
          </Paper>

          {(indicationItems.some((i) => ['surgery', 'procedure'].includes(String(i.act_type || '')))
            || inventoryConsumption.length > 0
            || inventoryAlreadyApplied) && (
            <EvolutionInventoryConsumptionSection
              patientId={form.patient_id || patient?.id}
              procedureIds={indicationItems.map((i) => i.procedure_id).filter(Boolean) as string[]}
              procedureSessions={indicationItems
                .filter((i) => i.procedure_id)
                .map((i) => ({
                  procedure_id: i.procedure_id as string,
                  session_number: (i as any).session_number ?? (i as any).procedure_session_number ?? null,
                }))}
              disabled={contentDisabled}
              alreadyApplied={inventoryAlreadyApplied}
              initialLines={inventoryConsumption}
              value={inventoryConsumption}
              onChange={setInventoryConsumption}
            />
          )}

          <EvolutionOutcomeSection
            compact={compactLayout}
            outcome={outcome}
            onChange={setOutcome}
            indicationItems={indicationItems}
            printables={attachedPrintables}
            showValidation={outcomeValidationOn}
            disabled={contentDisabled}
          />

          {/* Sinais vitais movidos para a Pré-consulta/triagem do cadastro do paciente
              (sugestão médica — PDF 29.05). Mantido o state/payload para não perder dados
              de evoluções antigas; apenas oculto da edição na evolução. */}
          {false && (
          <Accordion
            defaultExpanded={false}
            sx={{
              mb: 1,
              borderRadius: 2,
              '&:before': { display: 'none' },
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreAccordionIcon />} sx={{ px: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Sinais vitais e complementar</Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  PA, FC, peso, notas internas e retorno em texto livre
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                Preencha apenas o que foi aferido. IMC calculado quando peso e altura existirem.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="PA sistólica (mmHg)"
                    type="number"
                    value={form.vital_signs?.blood_pressure_systolic || ''}
                    onChange={(e) => updateVital('blood_pressure_systolic', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HeartIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="PA diastólica (mmHg)"
                    type="number"
                    value={form.vital_signs?.blood_pressure_diastolic || ''}
                    onChange={(e) => updateVital('blood_pressure_diastolic', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HeartIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="FC (bpm)"
                    type="number"
                    value={form.vital_signs?.heart_rate || ''}
                    onChange={(e) => updateVital('heart_rate', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SpeedIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="FR (irpm)"
                    type="number"
                    value={form.vital_signs?.respiratory_rate || ''}
                    onChange={(e) => updateVital('respiratory_rate', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LungIcon sx={{ color: EVOLUTION_THEME.vitalsAccent, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="Temperatura (°C)"
                    type="number"
                    value={form.vital_signs?.temperature || ''}
                    onChange={(e) => updateVital('temperature', e.target.value)}
                    inputProps={{ step: 0.1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TempIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="SpO₂ (%)"
                    type="number"
                    value={form.vital_signs?.oxygen_saturation || ''}
                    onChange={(e) => updateVital('oxygen_saturation', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BloodIcon sx={{ color: EVOLUTION_THEME.vitalsAccentAlt, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 0.5 }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="Peso (kg)"
                    type="number"
                    value={form.vital_signs?.weight || ''}
                    onChange={(e) => updateVital('weight', e.target.value)}
                    inputProps={{ step: 0.1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WeightIcon sx={{ color: EVOLUTION_THEME.vitalsAccent, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="Altura (cm)"
                    type="number"
                    value={form.vital_signs?.height || ''}
                    onChange={(e) => updateVital('height', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HeightIcon sx={{ color: EVOLUTION_THEME.vitalsAccent, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="IMC"
                    type="number"
                    disabled
                    value={
                      form.vital_signs?.weight && form.vital_signs?.height
                        ? (form.vital_signs.weight / Math.pow(form.vital_signs.height / 100, 2)).toFixed(1)
                        : ''
                    }
                    helperText={(() => {
                      if (!form.vital_signs?.weight || !form.vital_signs?.height) {
                        return 'Calculado automaticamente';
                      }
                      const bmi = form.vital_signs.weight / Math.pow(form.vital_signs.height / 100, 2);
                      if (bmi < 18.5) return 'Abaixo do peso';
                      if (bmi < 25) return 'Peso normal';
                      if (bmi < 30) return 'Sobrepeso';
                      return 'Obesidade';
                    })()}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="Glicemia (mg/dL)"
                    type="number"
                    value={form.vital_signs?.blood_glucose || ''}
                    onChange={(e) => updateVital('blood_glucose', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    label="Escala de dor (0–10)"
                    type="number"
                    value={form.vital_signs?.pain_scale ?? ''}
                    onChange={(e) => updateVital('pain_scale', e.target.value)}
                    inputProps={{ min: 0, max: 10 }}
                    helperText={(() => {
                      const pVal = form.vital_signs?.pain_scale;
                      if (pVal === undefined || pVal === null || pVal === '') return '';
                      if (pVal <= 3) return 'Dor leve';
                      if (pVal <= 6) return 'Dor moderada';
                      return 'Dor intensa';
                    })()}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    multiline
                    minRows={2}
                    maxRows={10}
                    label="Observações internas"
                    value={form.internal_notes}
                    onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
                    placeholder="Notas para a equipe (não destinadas ao paciente)"
                    helperText="Visível apenas na clínica"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    multiline
                    minRows={2}
                    maxRows={10}
                    label="Orientações de retorno (texto livre)"
                    value={form.follow_up_notes}
                    onChange={(e) => setForm({ ...form, follow_up_notes: e.target.value })}
                    placeholder="Combine com as opções de retorno acima, se desejar"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={contentDisabled}
                    type="date"
                    label="Data prevista de retorno"
                    value={form.follow_up_date}
                    onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

              </Grid>
            </AccordionDetails>
          </Accordion>
          )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={evolutionDialogFooterSxFor(compactLayout)}>
          {((contentDisabled && existingEvolutionId && loadedEvolutionSnapshot) ||
            (!contentDisabled && !loadedIsInvalidated)) && (
            <Box sx={evolutionStatusBannerSx(isSignedState, compactLayout)}>
              <Typography variant="caption" sx={evolutionStatusBannerTextSxFor(isSignedState, compactLayout)}>
                {contentDisabled && existingEvolutionId && loadedEvolutionSnapshot
                  ? evolutionEditTooltip(evolutionViewer, loadedEvolutionSnapshot)
                  : draftStatusText
                    ? (
                      <>
                        {draftStatusText}
                        {draftSaveStatus === 'error' && draftLastError
                          ? ` — ${draftLastError}. Tentaremos de novo na próxima alteração.`
                          : ''}
                      </>
                    )
                    : isSignedState
                      ? (() => {
                          const deadline = evolutionEditDeadline(loadedEvolutionSnapshot);
                          const until = deadline
                            ? ` até ${deadline.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                            : ' por 24h após a conclusão';
                          const full = `Evolução concluída: você pode continuar editando${until}. As alterações são salvas automaticamente.`;
                          return compactLayout ? `Concluída — edição automática${until}.` : full;
                        })()
                      : compactLayout
                        ? 'Rascunho salvo automaticamente. «Concluir» para finalizar.'
                        : 'Rascunho salvo automaticamente enquanto você preenche. Use «Concluir» para finalizar.'}
              </Typography>
            </Box>
          )}
          <Stack
            direction="row"
            spacing={compactLayout ? 0.75 : 1}
            useFlexGap
            flexWrap="wrap"
            justifyContent="flex-end"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Button size={compactLayout ? 'small' : 'medium'} onClick={() => void handleClose()} sx={evolutionCancelButtonSx}>
              {embedded ? 'Ocultar painel' : 'Cancelar'}
            </Button>
            <Tooltip title="Evolução, indicações e cada impresso em páginas separadas">
              <span>
                <Button
                  size={compactLayout ? 'small' : 'medium'}
                  variant="outlined"
                  startIcon={<PrintIcon fontSize={compactLayout ? 'small' : 'medium'} />}
                  disabled={!evolutionHasFullPrintContent(getPrintSnapshot())}
                  onClick={() => void printEvolutionComplete()}
                  sx={evolutionOutlinedButtonSx}
                >
                  Documento completo
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Apenas impressos, solicitações e indicações (sem o texto da evolução)">
              <span>
                <Button
                  size={compactLayout ? 'small' : 'medium'}
                  variant="outlined"
                  disabled={!evolutionHasBatchPrintContent(getPrintSnapshot())}
                  startIcon={<LayersIcon fontSize={compactLayout ? 'small' : 'medium'} />}
                  onClick={() => void printEvolutionBatch()}
                  sx={evolutionSecondaryOutlinedButtonSx}
                >
                  Imprimir todos
                </Button>
              </span>
            </Tooltip>
            <Button
              size={compactLayout ? 'small' : 'medium'}
              variant="contained"
              onClick={handleSave}
              disabled={saving || contentDisabled}
              startIcon={saving ? <CircularProgress size={compactLayout ? 14 : 16} color="inherit" /> : null}
              sx={{
                ...evolutionPrimaryButtonSx,
                ...(compactLayout ? { minWidth: 108, fontWeight: 700 } : {}),
              }}
            >
              {saving ? 'Salvando…' : isSignedState ? 'Salvar e fechar' : 'Concluir'}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <EvolutionPrintableEditHost
        target={editPrintableTarget}
        onClose={() => setEditPrintableTarget(null)}
        onSave={(updated) => {
          if (indicationPrintable?.clientKey === updated.clientKey) {
            setIndicationPrintable(updated);
            void saveDraftWithOverrides({ indicationPrintable: updated });
          } else {
            updateAttachedPrintable(updated);
          }
        }}
        patientId={form.patient_id}
        professionalId={form.professional_id}
        initialPatient={patient}
        initialProfessional={initialProfessional}
      />
      {!isFisioterapia && (
      <EvolutionPrintablesWizard
        open={printWizardOpen}
        onClose={() => setPrintWizardOpen(false)}
        patientId={form.patient_id}
        professionalId={form.professional_id}
        initialPatient={patient}
        initialProfessional={initialProfessional}
        onCommit={(items) => {
          const batch = Array.isArray(items) ? items : [items];
          if (!batch.length) return;
          setAttachedPrintables((prev) => {
            const next = [...prev, ...batch];
            void saveDraftWithOverrides({ attachedPrintables: next });
            return next;
          });
        }}
      />
      )}
      <EvolutionIndicationsDialog
        open={indicationsDialogOpen}
        onClose={() => setIndicationsDialogOpen(false)}
        onFinish={({ items, printable, gridSelections }) => {
          const nextItems = mergeEvolutionIndicationItems(indicationItems, items || []);
          const nextLabels = nextItems.map((i) => i.name);
          const nextIndication = printable ?? indicationPrintable;
          const nextSelections = gridSelections ?? indicationSelections;
          setIndicationItems(nextItems);
          setProcedureLabels(nextLabels);
          setIndicationSelections(nextSelections);
          if (printable) setIndicationPrintable(printable);
          void saveDraftWithOverrides({
            procedureLabels: nextLabels,
            indicationItems: nextItems,
            indicationSelections: nextSelections,
            indicationPrintable: nextIndication,
          });
        }}
      />


      <EvolutionScheduleReturnPrompt
        open={Boolean(scheduleReturnPrompt)}
        patientId={scheduleReturnPrompt?.patientId || ''}
        patientName={scheduleReturnPrompt?.patientName}
        professionalId={scheduleReturnPrompt?.professionalId}
        returnDate={scheduleReturnPrompt?.suggestion.appointmentDate || ''}
        summary={scheduleReturnPrompt?.suggestion.summary || ''}
        appointmentType={scheduleReturnPrompt?.suggestion.appointmentType}
        onClose={() => {
          setScheduleReturnPrompt(null);
          if (!embedded) {
            setAutosaveEvolutionId(null);
            onClose();
          }
        }}
      />

      <DocumentTextTemplateManagerDialog
        open={evolutionTemplateManagerOpen}
        onClose={() => setEvolutionTemplateManagerOpen(false)}
        category="evolution"
        professionalId={user?.professional_id}
        onTemplatesChanged={() => void loadEvolutionTemplates()}
        onApply={applyEvolutionTemplate}
      />

      <Snackbar
        open={aiSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setAiSnackbar({ ...aiSnackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setAiSnackbar({ ...aiSnackbar, open: false })}
          severity={aiSnackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {aiSnackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default HealthcareEvolutionDialog;
